import React, { useState, useEffect } from 'react';
import { DoughBatch, DoughProductName, DoughIngredientInput, DoughRecipe } from '../../../types/doughBatch';
import { DoughIngredientsTable, DoughIngredient } from './DoughIngredientsTable';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { fetchDoughBatchIngredientsByDoughBatchId } from '../../../store/slices/doughBatchIngredientSlice';
import { doughBatchApiService } from '../../../services/doughBatchApiService';

interface DoughRecordCardProps {
  record: DoughBatch;
  isEditMode: boolean;
  onUpdate: (record: DoughBatch) => void;
  onSaveNew?: (record: DoughBatch, ingredients: DoughIngredientInput[]) => void;
  onDelete: () => void;
  onEditToggle: () => void;
  onDuplicate?: () => void;
  onTimesProducedChange?: (id: string, timesProduced: number) => void;
}

export const DoughRecordCard: React.FC<DoughRecordCardProps> = ({
  record,
  isEditMode,
  onUpdate,
  onSaveNew,
  onDelete,
  onEditToggle,
  onDuplicate,
  onTimesProducedChange
}) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isIngredientsExpanded, setIsIngredientsExpanded] = useState(false);
  const [editedRecord, setEditedRecord] = useState<DoughBatch>(record);
  const [localIngredients, setLocalIngredients] = useState<DoughIngredient[]>([]);
  const [availableRecipes, setAvailableRecipes] = useState<DoughRecipe[]>([]);

  // Determine if this is a new/temporary record
  const isTempRecord = record.id.startsWith('temp-');

  // Get ingredients from Redux state filtered by this batch's ID
  const reduxIngredients = useAppSelector(state =>
    state.doughBatchIngredients.ingredients.filter(
      ing => ing.doughBatchId === record.id
    )
  );

  // Fetch available recipes on mount
  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const recipes = await doughBatchApiService.fetchDoughRecipes();
        setAvailableRecipes(recipes);
      } catch (error) {
        console.error('Error fetching dough recipes:', error);
      }
    };
    fetchRecipes();
  }, []);

  // Fetch ingredients when card is for a persisted record (non-temp ID)
  useEffect(() => {
    if (!isTempRecord && record.id) {
      dispatch(fetchDoughBatchIngredientsByDoughBatchId(record.id));
    }
  }, [dispatch, record.id, isTempRecord]);

  // Auto-populate ingredients when productName changes on temp record
  useEffect(() => {
    if (isTempRecord && editedRecord.productName) {
      const fetchRecipeIngredients = async () => {
        try {
          const recipeIngredients = await doughBatchApiService.fetchDoughRecipe(editedRecord.productName);
          const ingredients: DoughIngredient[] = recipeIngredients.map((item, index) => ({
            id: `temp-ing-${index}`,
            ingredientName: item.ingredientName,
            lotNumbers: [],
            quantity: item.quantity,
            unit: item.unit
          }));
          setLocalIngredients(ingredients);
        } catch (error) {
          console.error('Error fetching recipe ingredients:', error);
        }
      };
      fetchRecipeIngredients();
    }
  }, [isTempRecord, editedRecord.productName]);

  // Use local ingredients for temp records, Redux for persisted
  const displayIngredients: DoughIngredient[] = isTempRecord
    ? localIngredients
    : reduxIngredients.map(ing => ({
        id: ing.id,
        ingredientName: ing.ingredientName,
        lotNumbers: ing.lotNumber ? [ing.lotNumber] : [],
        quantity: ing.quantity ?? undefined,
        unit: ing.unit ?? undefined
      }));

  // Product name options from available recipes
  const productOptions: DoughProductName[] = availableRecipes.map(r => r.name);

  const handleFieldChange = (field: keyof DoughBatch, value: string) => {
    setEditedRecord({
      ...editedRecord,
      [field]: value
    });
  };

  const handleSave = () => {
    // For new records, use onSaveNew with ingredients
    if (isTempRecord && onSaveNew) {
      // Convert local ingredients to the input format
      const ingredientInputs: DoughIngredientInput[] = localIngredients.map(ing => ({
        ingredientName: ing.ingredientName,
        lotNumbers: ing.lotNumbers,
        quantity: ing.quantity,
        unit: ing.unit
      }));
      onSaveNew(editedRecord, ingredientInputs);
    } else {
      onUpdate(editedRecord);
    }
  };

  const handleCancel = () => {
    setEditedRecord(record);
    onEditToggle();
  };

  const handleIngredientChange = (
    id: string,
    field: keyof DoughIngredient,
    value: string | string[] | number | undefined
  ) => {
    setLocalIngredients(prev =>
      prev.map(ing =>
        ing.id === id ? { ...ing, [field]: value } : ing
      )
    );
  };

  // Get current timesProduced with fallback to 1
  const currentTimesProduced = record.timesProduced ?? 1;

  // Debug: Log prop status
  console.log('[DoughRecordCard] Debug:', {
    recordId: record.id,
    isTempRecord,
    currentTimesProduced,
    hasOnTimesProducedChange: !!onTimesProducedChange
  });

  const handleTimesProducedChange = (newValue: number) => {
    console.log('[DoughRecordCard] handleTimesProducedChange called with:', newValue);
    // Ensure minimum value of 1
    const value = Math.max(1, newValue);
    console.log('[DoughRecordCard] After Math.max:', value, 'onTimesProducedChange:', !!onTimesProducedChange, 'isTempRecord:', isTempRecord);
    if (onTimesProducedChange && !isTempRecord) {
      console.log('[DoughRecordCard] Calling onTimesProducedChange with:', record.id, value);
      onTimesProducedChange(record.id, value);
    } else {
      console.log('[DoughRecordCard] NOT calling onTimesProducedChange - condition failed');
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  };

  return (
    <div
      style={{
        border: '2px solid #ddd',
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: 'white',
        overflow: 'hidden'
      }}
    >
      {/* Card Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
      >
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{record.productName || 'New Dough Record'}</h4>
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}
        >
          ▼
        </span>
      </div>

      {/* Card Content */}
      <div style={{ padding: '20px' }}>
        {/* Table of Fields */}
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Product Name
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Date of Production
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Batch/Lot #
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Prepared By
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Verified By
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                  {isEditMode ? (
                    <select
                      value={editedRecord.productName}
                      onChange={e => handleFieldChange('productName', e.target.value)}
                      style={inputStyle}
                    >
                      {productOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    record.productName || '-'
                  )}
                </td>
                <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                  {isEditMode ? (
                    <input
                      type="date"
                      value={editedRecord.dateOfProduction?.split('T')[0] || ''}
                      readOnly
                      style={{
                        ...inputStyle,
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed'
                      }}
                    />
                  ) : (
                    record.dateOfProduction ? record.dateOfProduction.split('T')[0] : '-'
                  )}
                </td>
                <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedRecord.batchNumber || ''}
                      readOnly
                      style={{
                        ...inputStyle,
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed'
                      }}
                      placeholder="Auto-generated on save"
                    />
                  ) : (
                    record.batchNumber || '-'
                  )}
                </td>
                <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedRecord.preparedBy}
                      onChange={e => handleFieldChange('preparedBy', e.target.value)}
                      style={inputStyle}
                      placeholder="Prepared by"
                    />
                  ) : (
                    record.preparedBy || '-'
                  )}
                </td>
                <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedRecord.verifiedBy}
                      onChange={e => handleFieldChange('verifiedBy', e.target.value)}
                      style={inputStyle}
                      placeholder="Verified by"
                    />
                  ) : (
                    record.verifiedBy || '-'
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Expanded View - Ingredients and Preparation Section */}
        {isExpanded && (
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div
                onClick={() => setIsIngredientsExpanded(!isIngredientsExpanded)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: isIngredientsExpanded ? '1px solid #e0e0e0' : 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#555',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                Ingredients and Preparation
                <span
                  style={{
                    fontSize: '1rem',
                    color: '#666',
                    transition: 'transform 0.2s ease-in-out',
                    transform: isIngredientsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}
                >
                  ▶
                </span>
              </div>
              {isIngredientsExpanded && (
                <div
                  style={{
                    padding: '20px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <DoughIngredientsTable
                    ingredients={displayIngredients}
                    isEditing={isEditMode || isTempRecord}
                    onIngredientChange={handleIngredientChange}
                    timesProduced={currentTimesProduced}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isEditMode ? (
              <>
                <button
                  onClick={handleSave}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#4CAF50',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#757575',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onEditToggle}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#2196F3',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
                >
                  Edit
                </button>
                {onDuplicate && (
                  <button
                    onClick={onDuplicate}
                    style={{
                      ...buttonStyle,
                      backgroundColor: '#9C27B0',
                      color: 'white'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#7B1FA2')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#9C27B0')}
                  >
                    Duplicate
                  </button>
                )}
                <button
                  onClick={() => {
                    if (window.confirm('Delete this dough record? This cannot be undone.')) {
                      onDelete();
                    }
                  }}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#f44336',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                >
                  Delete
                </button>
              </>
            )}
          </div>

          {/* Times Produced Control - always visible, not dependent on edit mode */}
          {!isTempRecord && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#555', whiteSpace: 'nowrap' }}>
                # of times produced:
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[DoughRecordCard] MINUS button clicked, currentTimesProduced:', currentTimesProduced);
                    handleTimesProducedChange(currentTimesProduced - 1);
                  }}
                  disabled={currentTimesProduced <= 1}
                  style={{
                    ...buttonStyle,
                    padding: '4px 10px',
                    backgroundColor: currentTimesProduced <= 1 ? '#cccccc' : '#757575',
                    color: 'white',
                    cursor: currentTimesProduced <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={e => {
                    if (currentTimesProduced > 1) {
                      e.currentTarget.style.backgroundColor = '#616161';
                    }
                  }}
                  onMouseLeave={e => {
                    if (currentTimesProduced > 1) {
                      e.currentTarget.style.backgroundColor = '#757575';
                    }
                  }}
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  value={currentTimesProduced}
                  onClick={(e) => e.stopPropagation()}
                  onChange={e => {
                    e.stopPropagation();
                    console.log('[DoughRecordCard] INPUT changed, raw value:', e.target.value);
                    const value = parseInt(e.target.value, 10);
                    console.log('[DoughRecordCard] INPUT parsed value:', value);
                    if (!isNaN(value)) {
                      handleTimesProducedChange(value);
                    }
                  }}
                  style={{
                    width: '50px',
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[DoughRecordCard] PLUS button clicked, currentTimesProduced:', currentTimesProduced);
                    handleTimesProducedChange(currentTimesProduced + 1);
                  }}
                  style={{
                    ...buttonStyle,
                    padding: '4px 10px',
                    backgroundColor: '#757575',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                >
                  +
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
